/**
 * THE AUTOSAVE, AND THE CONFLICT IT IS BUILT AROUND.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ THE 409 IS A SCREEN, NOT A TOAST — WHICH IS WHY THIS HOOK KEEPS TWO DOCUMENTS
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * A stale `baseRevision` is answered **409 `AUTOSAVE_REVISION_STALE` carrying the server's WHOLE
 * working copy** (authors-api.yaml:1036-1039, `docs/ui/04-pages.md:655`: *"a normal outcome, not an
 * error screen … it arrives carrying two versions of the author's work"*). So on a conflict this
 * hook does exactly one thing: it stops saving and holds BOTH copies — the author's unsaved draft
 * and the server's — until the author picks. It never merges, it never retries, and it never
 * re-fetches: the contract removed the second round trip on purpose, and a hook that threw the body
 * away and re-read would have put it back.
 *
 * ★ THE 200 DOES NOT ECHO `blocks`, so an accepted save updates the REVISION and nothing else. A
 * hook that replaced its document from the response would replace the author's newest keystrokes
 * with the ones it sent 400 ms ago.
 *
 * ★ NO STRUCTURAL LIMIT IS ENFORCED AT AUTOSAVE — "a draft may be half written"
 * (docs/design/09-authors-app.md:288). Nothing here blocks a save on a short article; the checklist
 * is advisory until Submit, which is the single place D21 is enforced.
 *
 * ★ DEBOUNCED, AND THE DEBOUNCE IS A `setTimeout` AND NOT A CLOCK READ. `eslint.config.mjs:41-60`
 * bans `Date.now()` and zero-argument `new Date()` across `web/` (C11); a timer is neither. The
 * whole operation sits inside a 300 ms budget server-side (`02-nfr.md:120`), which is why this
 * fires on a pause rather than on every keystroke.
 *
 * ★ ONE CATEGORY TICKED IS ONE ADDRESS CATEGORY (design 14, F8). Every edit, and every copy this
 * hook opens, goes through `chooseSoleAddressCategory` (./address.ts) while the author may write:
 * an edit that leaves exactly one category and no pick makes that category the pick, and the save
 * that edit schedules carries it like any other change. A copy OPENED that way (saved before this
 * rule, or imported) is given the pick on screen at once but schedules nothing - opening is not an
 * edit - so the next autosave carries it, and Send and "Keep this version", which save first,
 * always do. A copy nobody may write is shown exactly as the platform holds it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { isAutosaveConflict, useEditorApi } from '../../../features/cabinet/api/editor-api';
import { baseOf, draftOf, isSourceLanguageRefusal, sourceLanguageChange } from './document';
import type { AutosaveRequest, WorkingCopy } from '../../../features/cabinet/api/editor-api';
import type { DraftBase, EditorDraft } from './document';
import {
  chooseSoleAddressCategory,
  isSlugRefusal,
  slugChange,
  slugRefusalMessage,
} from './address';
/** How long a pause counts as "stopped typing". */
export const AUTOSAVE_DEBOUNCE_MS = 900;

export type SaveState =
  | 'clean'
  /** Edited, and the debounce has not elapsed. */
  | 'pending'
  | 'saving'
  | 'saved'
  /** The save was refused. `error` says which refusal; a conflict sets `conflict` as well. */
  | 'failed';

export interface EditorDraftState {
  readonly draft: EditorDraft;
  readonly base: DraftBase;
  readonly saveState: SaveState;
  readonly error: unknown;
  /** The server's copy, present only while a conflict is unresolved. */
  readonly conflict: WorkingCopy | undefined;
  /**
   * The platform refused a change of the source language (its uncoded 422): the pick was put back
   * and the rest of that save went again without it. It stays true for the visit - the language is
   * fixed, and the picker says so rather than inviting the same refusal again.
   */
  readonly languageRefused: boolean;
  /**
   * The platform refused the web address a save carried (R-019: taken, fixed, or no address
   * category): the field was put back to the address it holds and the rest of that save went again
   * without it. The sentence for the Search pane, until a save carrying an address lands.
   */
  readonly slugRefusal: string | undefined;
  /** Apply a change. Schedules a save unless the article is not writable. */
  readonly edit: (change: (draft: EditorDraft) => EditorDraft) => void;
  /**
   * Save now, without waiting for the debounce. Used by Submit, which must save first. A save
   * already in flight is waited for, then the newest draft is sent unless that save carried it.
   */
  readonly saveNow: () => Promise<boolean>;
  /** Conflict resolution: discard mine and take the server's copy whole. */
  readonly takeServerCopy: () => void;
  /** Conflict resolution: keep mine and re-base onto the server's revision, then save. */
  readonly keepMyCopy: () => void;
  /** After a transition, the article's status has moved and the copy must be re-read. */
  readonly adopt: (copy: WorkingCopy) => void;
  /**
   * A cover the platform has just stored - `claimArticleImageUpload`'s answer. The draft names it,
   * so the next save carries its `coverFileId`, and the screen shows it at once.
   */
  readonly setCover: (image: NonNullable<WorkingCopy['cover']>) => void;
}

/**
 * `WorkingCopySaved.writableBy` is declared `string | null` while `WorkingCopy.writableBy` is the
 * closed three-member enum, so the autosave response cannot be assigned across without a check.
 * Narrowed rather than cast: an unrecognised value leaves the screen on the value it already had,
 * which is the safe direction — a cast would let an unknown string disable or enable every control.
 */
function asWritableBy(value: string | null | undefined): DraftBase['writableBy'] | undefined {
  return value === 'author' || value === 'admin' || value === 'nobody' ? value : undefined;
}

/**
 * The document a copy opens as - see the header: the sole category is the pick where the author
 * may write, and nowhere else, so a piece in review never shows a pick the platform does not hold.
 */
function openedDraft(copy: WorkingCopy): EditorDraft {
  const opened = draftOf(copy);
  return baseOf(copy).writableBy === 'author' ? chooseSoleAddressCategory(opened) : opened;
}

/** The request body, built at the boundary: empty strings become the nulls the wire wants. */
function toRequest(draft: EditorDraft, base: DraftBase): AutosaveRequest {
  const blank = (value: string): string | null => (value.trim().length === 0 ? null : value);
  const sourceLanguage = sourceLanguageChange(draft, base);
  const slug = slugChange(draft, base);
  return {
    baseRevision: base.revision,
    title: draft.title,
    subtitle: draft.subtitle,
    coverFileId: draft.coverFileId,
    seoTitle: blank(draft.seoTitle),
    metaDescription: blank(draft.metaDescription),
    // ★ ONLY A CHANGE IS SENT (./document.ts, `sourceLanguageChange`): absent keeps the stored one.
    ...(sourceLanguage === undefined ? {} : { sourceLanguage }),
    // ★ NO CONTROL WRITES THESE TWO (R-004, ./SetupPanel.tsx): what was loaded goes back unchanged.
    articleFormat: blank(draft.articleFormat),
    timeSensitivity: blank(draft.timeSensitivity),
    plannedGoLiveAt: draft.plannedGoLiveAt,
    categories: draft.categories,
    addressCategory: draft.addressCategory,
    tags: draft.tags,
    blocks: draft.blocks,
    // ★ THE ADDRESS, ONLY WHEN IT CHANGES (./address.ts, `slugChange`, R-019): absent keeps the one
    // the platform holds, and every address a save reserves stays taken for others for ever.
    ...(slug === undefined ? {} : { slug }),
  };
}

/** How one save's flight ended, for whoever waited on it - `saveNow`. */
interface Landing {
  /** The 200 was taken in: the revision moved. False on a refusal, a conflict or a dead screen. */
  readonly landed: boolean;
  /** The document that flight carried. */
  readonly sent: EditorDraft;
  /** Refused for its language or address alone, and already sent again without it - wait for it. */
  readonly resent: boolean;
}

export function useEditorDraft(initial: WorkingCopy): EditorDraftState {
  const api = useEditorApi();
  const [draft, setDraft] = useState<EditorDraft>(() => openedDraft(initial));
  const [base, setBase] = useState<DraftBase>(() => baseOf(initial));
  const [saveState, setSaveState] = useState<SaveState>('clean');
  const [error, setError] = useState<unknown>(undefined);
  const [conflict, setConflict] = useState<WorkingCopy | undefined>(undefined);
  const [languageRefused, setLanguageRefused] = useState(false);
  const [slugRefusal, setSlugRefusal] = useState<string | undefined>(undefined);

  /* The newest values, for the timer callback. A `setTimeout` closure captures the render it was
     created in, and the whole point of a debounce is that several renders happen inside one. */
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const baseRef = useRef(base);
  baseRef.current = base;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const liveRef = useRef(true);
  /* One save in flight at a time. A second PUT sent before the first answers would carry the same
     stale `baseRevision` and manufacture the very conflict this screen renders. It holds the flight
     itself, so `saveNow` can wait for it to land. */
  const inFlight = useRef<Promise<Landing> | null>(null);
  /* ★ A SAVE ASKED FOR WHILE ONE IS IN FLIGHT IS OWED, NOT DROPPED. The debounce fires on its own
     clock, so on a save slower than `AUTOSAVE_DEBOUNCE_MS` the next edit's timer lands mid-flight.
     It used to return `false` there and nothing asked again - the edit waited for the NEXT edit to
     be sent, while the badge said "Draft saved" (found by the wave-11 editor writer, 2026-09-23).
     Now it is remembered, and paid the moment the flight lands well; a flight that failed or met
     a conflict pays nothing, because the author has to see that first. */
  const owed = useRef(false);

  useEffect(() => {
    /* ★ SET ON EVERY MOUNT, NOT ONLY AT CREATION. StrictMode (src/main.tsx) mounts, unmounts and
       re-mounts every component in development, and the ref survives that - so a guard set only by
       the cleanup stayed `false` for the whole visit: every save's answer was dropped, the revision
       never moved, the SECOND save was a silent 409 and "Saving..." never cleared. Found live on
       2026-09-23 adding a picture after a cover; no spec rendered under StrictMode. */
    liveRef.current = true;
    return () => {
      liveRef.current = false;
      if (timer.current !== undefined) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (baseRef.current.writableBy !== 'author') {
      return false;
    }
    if (inFlight.current !== null) {
      owed.current = true;
      return false;
    }
    let land: (landing: Landing) => void = () => undefined;
    inFlight.current = new Promise<Landing>((resolve) => {
      land = resolve;
    });
    setSaveState('saving');
    setError(undefined);
    let landed = false;
    let resent = false;
    const sent = draftRef.current;
    const request = toRequest(sent, baseRef.current);
    try {
      const saved = await api.autosaveArticle(baseRef.current.articleId, request);
      if (!liveRef.current) {
        return true;
      }
      /* ONLY THE SERVER'S OWN MEMBERS MOVE. The document stays exactly as the author has it — the
         200 does not echo `blocks`, and adopting the request we just sent would drop whatever was
         typed while it was in flight. */
      const nextBase: DraftBase = {
        ...baseRef.current,
        revision: saved.revision,
        updatedAt: saved.updatedAt,
        // The language this request carried is the stored one now; none sent left it as it was.
        savedSourceLanguage: request.sourceLanguage ?? baseRef.current.savedSourceLanguage,
        // The address the platform now holds is ITS word (`WorkingCopySaved.slug`): a changed
        // address category moves a held address only where it is free there (R-019).
        savedSlug:
          saved.slug !== undefined
            ? saved.slug
            : request.slug !== undefined
              ? request.slug
              : baseRef.current.savedSlug,
        // The verdict on the cover THIS request carried. A cover set while it was in flight has not
        // been judged yet (see `setCover`), and the save that edit scheduled will judge it.
        awaitingCover:
          draftRef.current.coverFileId === request.coverFileId
            ? (saved.awaitingCover ?? baseRef.current.awaitingCover)
            : baseRef.current.awaitingCover,
        writableBy: asWritableBy(saved.writableBy) ?? baseRef.current.writableBy,
      };
      baseRef.current = nextBase;
      setBase(nextBase);
      if (request.slug !== undefined) {
        setSlugRefusal(undefined);
      }
      // Saved - but only what was SENT. An edit made while it flew is still to go, and says so.
      setSaveState(draftRef.current === sent ? 'saved' : 'pending');
      landed = true;
      return true;
    } catch (cause) {
      owed.current = false;
      if (!liveRef.current) {
        return false;
      }
      if (request.slug !== undefined && isSlugRefusal(cause)) {
        /* ★ THE ADDRESS IS REFUSED BY NAME, AND IT TOO MUST NOT STALL THE DOCUMENT (C2-05's attack).
           Taken since the check, fixed by a publication, or sent with no address category: the
           field goes back to the address the platform holds, the pane says why, and the rest of
           this save goes again at once without it - the language's own rule, below. */
        const reverted: EditorDraft = {
          ...draftRef.current,
          slug: baseRef.current.savedSlug ?? '',
        };
        draftRef.current = reverted;
        setDraft(reverted);
        setSlugRefusal(slugRefusalMessage(cause));
        setSaveState('pending');
        resent = true;
        return false;
      }
      if (request.sourceLanguage !== undefined && isSourceLanguageRefusal(cause)) {
        /* ★ THE LANGUAGE IS FIXED, AND ONE REFUSED MEMBER MUST NOT STALL THE WHOLE DOCUMENT. The
           platform answers a change after the first version with its uncoded 422 (C2-03's
           attack), and a whole-document save that kept carrying the pick would be refused on
           every keystroke after it. So the pick is put back to the stored language, the picker
           says why, and the rest of this save goes again at once without it - nothing typed is
           lost. The 422 declares no code, so it counts only on a save that carried a change. */
        const reverted: EditorDraft = {
          ...draftRef.current,
          sourceLanguage: baseRef.current.savedSourceLanguage,
        };
        draftRef.current = reverted;
        setDraft(reverted);
        setLanguageRefused(true);
        setSaveState('pending');
        resent = true;
        return false;
      }
      if (isAutosaveConflict(cause)) {
        // ★ HOLD BOTH. Nothing is merged and nothing is discarded until the author says which.
        setConflict(cause.current);
      }
      setError(cause);
      setSaveState('failed');
      return false;
    } finally {
      inFlight.current = null;
      land({ landed, sent, resent });
      if (liveRef.current && ((landed && owed.current) || resent)) {
        owed.current = false;
        void save();
      }
    }
  }, [api]);

  const schedule = useCallback(() => {
    if (timer.current !== undefined) {
      clearTimeout(timer.current);
    }
    setSaveState('pending');
    timer.current = setTimeout(() => {
      timer.current = undefined;
      void save();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [save]);

  const edit = useCallback(
    (change: (current: EditorDraft) => EditorDraft) => {
      setDraft((current) => {
        const changed = change(current);
        // ★ THE SOLE CATEGORY IS THE PICK (header, F8) - inside this edit, so its save carries it.
        const next =
          baseRef.current.writableBy === 'author' ? chooseSoleAddressCategory(changed) : changed;
        draftRef.current = next;
        return next;
      });
      if (baseRef.current.writableBy === 'author' && conflict === undefined) {
        schedule();
      }
    },
    [schedule, conflict],
  );

  const saveNow = useCallback(async (): Promise<boolean> => {
    const cancelDebounce = (): void => {
      if (timer.current !== undefined) {
        clearTimeout(timer.current);
        timer.current = undefined;
      }
    };
    cancelDebounce();
    /* ★ A SAVE ALREADY IN THE AIR IS WAITED FOR, NOT REFUSED. `save` answers `false` over a flight,
       and Send - which stops on `false` - did nothing at all: no transition, no message (found in
       wave 13; a picture's own autosave goes ~900 ms after it is in, just when Send is pressed).
       So wait for the flight, and for the owed one it may pay on landing; then send the newest
       draft unless the last flight carried it. One that did not land answers `false` as before:
       the conflict pane or the "not saved" alert is already saying why - except a flight refused
       for its language alone, which has already gone again without it and is waited for too. */
    let carried: EditorDraft | undefined;
    for (;;) {
      while (inFlight.current !== null) {
        const landing = await inFlight.current;
        if (!landing.landed) {
          if (landing.resent) {
            continue;
          }
          return false;
        }
        carried = landing.sent;
      }
      // An edit made while it waited is this save's to carry, not a timer's.
      cancelDebounce();
      if (carried === draftRef.current || (await save())) {
        return true;
      }
      // Refused. Only a save refused for its language or address alone is in the air again.
      if (inFlight.current === null) {
        return false;
      }
    }
  }, [save]);

  /* BOTH RESOLUTIONS READ `conflict` FROM STATE AND NOT FROM A `setConflict` UPDATER. React calls
     an updater twice under StrictMode, so a side effect inside one runs twice — and "save the
     document" is very much a side effect. */
  const takeServerCopy = useCallback(() => {
    if (conflict === undefined) {
      return;
    }
    const nextDraft = openedDraft(conflict);
    const nextBase = baseOf(conflict);
    draftRef.current = nextDraft;
    baseRef.current = nextBase;
    setDraft(nextDraft);
    setBase(nextBase);
    setSaveState('clean');
    setError(undefined);
    setConflict(undefined);
    setSlugRefusal(undefined);
  }, [conflict]);

  const keepMyCopy = useCallback(() => {
    if (conflict === undefined) {
      return;
    }
    /* RE-BASE, DO NOT MERGE. The author's document is kept whole and re-sent against the server's
       current revision, which is what makes the next PUT accepted rather than a second conflict.
       The server's text is gone at the author's own instruction — the pane they pressed the button
       on had both documents on it, side by side. */
    const rebased: DraftBase = {
      ...baseRef.current,
      revision: conflict.revision,
      // What the platform holds now: my language goes with my document only where it differs.
      savedSourceLanguage: conflict.sourceLanguage ?? null,
      // And my address, the same way.
      savedSlug: conflict.slug ?? null,
    };
    baseRef.current = rebased;
    setBase(rebased);
    setConflict(undefined);
    setSaveState('pending');
    setError(undefined);
    void save();
  }, [conflict, save]);

  const adopt = useCallback((copy: WorkingCopy) => {
    const nextDraft = openedDraft(copy);
    const nextBase = baseOf(copy);
    setDraft(nextDraft);
    setBase(nextBase);
    draftRef.current = nextDraft;
    baseRef.current = nextBase;
    setSaveState('clean');
    setError(undefined);
    setConflict(undefined);
    setSlugRefusal(undefined);
  }, []);

  const setCover = useCallback(
    (image: NonNullable<WorkingCopy['cover']>) => {
      /* ★ THE RENDITION'S ADDRESS AND SIZE ARE THE PLATFORM'S OWN ANSWER, so they sit on the base
         beside its other members - and nothing else would ever put them there: an accepted save's
         200 carries no cover at all (`WorkingCopySaved`, authors-api.yaml:3220-3246), and this
         screen re-reads the copy only after a transition. So the new cover shows at once, and a
         conflict's "take theirs" or a re-read replaces it along with the rest of the base.

         `awaitingCover` IS THE SERVER'S VERDICT ON THE PREVIOUS COVER, and would put a red row on
         a picture it never saw. Cleared, the checklist measures the new cover's own dimensions
         against the policy's numbers (./document.ts, `cover_dimensions`) until the save this edit
         schedules answers with the server's verdict on it. */
      const nextBase: DraftBase = {
        ...baseRef.current,
        coverUrl: image.url,
        coverWidthPx: image.widthPx ?? null,
        coverHeightPx: image.heightPx ?? null,
        awaitingCover: false,
      };
      baseRef.current = nextBase;
      setBase(nextBase);
      edit((current) => ({ ...current, coverFileId: image.fileId }));
    },
    [edit],
  );

  return {
    draft,
    base,
    saveState,
    error,
    conflict,
    languageRefused,
    slugRefusal,
    edit,
    saveNow,
    takeServerCopy,
    keepMyCopy,
    adopt,
    setCover,
  };
}
